import React, { useState, useRef } from 'react';
import { X, Upload } from 'lucide-react';
import { Challenge } from '../../types/challenges';

interface ChallengeCompletionModalProps {
  challenge: Challenge;
  isOpen: boolean;
  onClose: () => void;
  onComplete: (imageFile: File, notes: string) => Promise<void>;
}

export function ChallengeCompletionModal({
  challenge,
  isOpen,
  onClose,
  onComplete
}: ChallengeCompletionModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!imageFile) {
      setError('Please upload a photo of your result');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await onComplete(imageFile, notes);
    } catch (err) {
      console.error('Error completing challenge:', err);
      setError('Failed to complete challenge. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[400] flex items-center justify-center p-4">
      <div className="bg-gradient-to-br from-[#1C1C1C] to-[#888888] shadow-lg backdrop-blur-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-white/10">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl text-white font-brand font-light italic">Complete Challenge</h2>
            <button 
              onClick={onClose}
              className="text-white/70 hover:text-white transition-colors p-1 hover:bg-white/10 rounded-full"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {error && (
            <div className="p-4 bg-red-500/10 backdrop-blur-sm text-red-200 rounded-xl text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-white/90 mb-2">
              Upload Result Photo
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageSelect}
              className="hidden"
              id="challenge-result"
            />
            
            {imagePreview ? (
              <div className="relative">
                <img
                  src={imagePreview}
                  alt="Challenge result preview"
                  className="w-full aspect-video object-cover rounded-xl"
                />
                <button
                  onClick={() => {
                    setImageFile(null);
                    setImagePreview(null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                  className="absolute top-2 right-2 p-1 bg-black/50 hover:bg-black/70 rounded-full text-white/90 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <label
                htmlFor="challenge-result"
                className="block w-full aspect-video border-2 border-dashed border-white/20 rounded-xl hover:border-amber-400/50 hover:bg-white/5 transition-colors cursor-pointer"
              >
                <div className="flex flex-col items-center justify-center h-full">
                  <Upload className="w-8 h-8 text-white/70 mb-2" />
                  <span className="text-white/70">Upload your challenge result</span>
                </div>
              </label>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-white/90 mb-2">
              Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Share your experience with this challenge..."
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder:text-white/50 focus:border-amber-300/50 focus:ring-2 focus:ring-amber-300/20 min-h-[100px]"
            />
          </div>
        </div>

        <div className="p-6 border-t border-white/10 bg-white/5">
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-6 py-2.5 text-white border border-white/20 rounded-xl hover:bg-white/10 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading || !imageFile}
              className={`flex items-center gap-2 px-6 py-2.5 text-white rounded-xl transition-all duration-300 ${
                loading || !imageFile
                  ? 'bg-white/10 text-white/50 cursor-not-allowed'
                  : 'bg-gradient-to-r from-amber-500/90 to-amber-600/90 hover:from-amber-500 hover:to-amber-600'
              }`}
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Completing...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Complete Challenge
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}